import { makeAutoObservable, runInAction } from 'mobx'

import { GoalApi } from '../api'
import { upsertByGuid } from '../utils/upsertByGuid'

import {
  type Goal,
  type MonthlyCashFlowProfile,
  type RetirementGoalAccount,
  TrackType,
} from '../types'
import { getProjectedRetirementDate } from '../utils/GoalsUtil'

import { GlobalStore } from './GlobalStore'

export class GoalStore {
  globalStore: GlobalStore
  api = new GoalApi('/', '')

  goals: Goal[] = []
  goalsLoaded: boolean = false
  retirementGoals: Goal[] = []
  retirementGoalsLoaded: boolean = false
  retirementGoalAccounts: RetirementGoalAccount[] = []
  retirementGoalAccountsLoaded: boolean = false
  monthlyCashFlowProfile: MonthlyCashFlowProfile | undefined = undefined
  monthlyCashFlowProfileLoaded: boolean = false

  newRetirementGoalAccounts: RetirementGoalAccount[] = []

  alert: string = ''
  selectedGoal: Goal | undefined = undefined

  constructor(globalStore: GlobalStore) {
    this.globalStore = globalStore
    this.api = new GoalApi(globalStore.endpoint, globalStore.sessionToken, globalStore.onError)

    makeAutoObservable(this)
  }

  get allGoals(): Goal[] {
    if (!this.isAllGoalDataLoaded) return []

    const goalsCopy = this.globalStore.copyStore.copy.goals
    const userBirthday = this.globalStore.userStore.user.birthday
    return [
      ...this.goals,
      ...this.retirementGoals.map((goal: Goal) => ({
        ...goal,
        amount: goal.target_amount,
        name: goalsCopy.retirement,
        track_type: TrackType.RETIREMENT_TRACK,
        projected_to_complete_at:
          goal.retirement_age && userBirthday
            ? getProjectedRetirementDate(goal.retirement_age, userBirthday)
            : undefined,
      })),
    ] as Goal[]
  }

  get orderedGoals(): Goal[] {
    return this.allGoals.slice().sort((a, b) => a.position - b.position)
  }

  get debtGoals() {
    return this.orderedGoals.filter((goal) => goal.track_type === TrackType.DEBT_TRACK)
  }

  get retirmentGoals() {
    return this.orderedGoals.filter((goal) => goal.track_type === TrackType.RETIREMENT_TRACK)
  }

  get savingGoals() {
    return this.orderedGoals.filter((goal) => goal.track_type === TrackType.SAVINGS_TRACK)
  }

  get ongoingGoalsForMicroWidget() {
    const ongoingGoals = this.allGoals.filter(
      (goal) =>
        !goal.is_complete &&
        goal.track_type !== TrackType.EMERGENCY_FUND_TRACK &&
        goal.track_type !== null,
    )
    const priorityOrder = [
      TrackType.SAVINGS_TRACK,
      TrackType.DEBT_TRACK,
      TrackType.RETIREMENT_TRACK,
    ]
    return ongoingGoals
      .sort((a, b) => {
        const aIndex = a.track_type ? priorityOrder.indexOf(a.track_type) : -1
        const bIndex = b.track_type ? priorityOrder.indexOf(b.track_type) : -1
        return aIndex - bIndex
      })
      .slice(0, 3)
  }

  get isAllGoalDataLoaded() {
    return (
      this.goalsLoaded &&
      this.retirementGoalsLoaded &&
      this.retirementGoalAccountsLoaded &&
      this.monthlyCashFlowProfileLoaded
    )
  }

  setGoals = (goals: Goal[]) => {
    this.goals = goals
    this.goalsLoaded = true
  }

  setRetirementGoals = (goals: Goal[]) => {
    this.retirementGoals = goals
    this.retirementGoalsLoaded = true
  }

  setRetirementGoalAccounts = (accounts: RetirementGoalAccount[]) => {
    this.retirementGoalAccounts = accounts
    this.retirementGoalAccountsLoaded = true
  }

  setMonthlyCashFlowProfile = (profile: MonthlyCashFlowProfile) => {
    this.monthlyCashFlowProfile = profile
    this.monthlyCashFlowProfileLoaded = true
  }

  loadGoalData = async () => {
    await Promise.all([
      this.loadGoals(),
      this.loadRetirementGoals(),
      this.loadRetirementGoalAccounts(),
      this.loadMonthlyCashFlowProfile(),
    ])
  }

  loadGoals = async () => {
    try {
      const goals = await this.api.getGoals()
      this.setGoals(goals)
    } catch (error) {
      console.error('Error occurred while getting goals:', error)
      this.globalStore.captureException(error, { store: 'GoalStore' })
    }
  }

  loadRetirementGoals = async () => {
    try {
      const retirementGoals = await this.api.getRetirementGoals()
      this.setRetirementGoals(retirementGoals)
    } catch (error) {
      console.error('Error occurred while loading retirement goals:', error)
      this.globalStore.captureException(error, { store: 'GoalStore' })
    }
  }

  loadRetirementGoalAccounts = async () => {
    try {
      const retirementGoalAccounts = await this.api.getRetirementGoalAccounts()
      this.setRetirementGoalAccounts(retirementGoalAccounts)
    } catch (error) {
      console.error('Error occurred while loading retirement goal accounts:', error)
      this.globalStore.captureException(error, { store: 'GoalStore' })
    }
  }

  loadMonthlyCashFlowProfile = async () => {
    try {
      const monthlyCashFlowProfile = await this.api.getMonthlyCashFlowProfile()
      this.setMonthlyCashFlowProfile(monthlyCashFlowProfile)
    } catch (error) {
      console.error('Error occurred while loading monthly cash flow profile:', error)
      this.globalStore.captureException(error, { store: 'GoalStore' })
    }
  }

  addGoal = async (goal: Goal) => {
    try {
      const newGoal = await this.api.addGoal(goal)
      this.setGoals(upsertByGuid(this.goals, newGoal))
    } catch (error) {
      console.error('Error occurred while adding goal:', error)
      this.globalStore.captureException(error, { store: 'GoalStore' })
    }
  }

  addRetirementGoal = async (goal: Goal) => {
    try {
      const newGoal: Goal = await this.api.addRetirementGoal(goal)
      const newAccounts = [] as RetirementGoalAccount[]
      for (const account of this.newRetirementGoalAccounts) {
        const newAccount = await this.api.addRetirementGoalAccount({
          ...account,
          retirement_goal_guid: newGoal.guid,
        })
        newAccounts.push(newAccount)
      }
      this.setNewRetirementGoalAccounts([])

      runInAction(() => {
        this.setRetirementGoals(upsertByGuid(this.retirementGoals, newGoal))
        this.setRetirementGoalAccounts(
          newAccounts.reduce((acc, a) => upsertByGuid(acc, a), this.retirementGoalAccounts),
        )
      })
    } catch (error) {
      console.error('Error occurred while adding retirement goal:', error)
      this.globalStore.captureException(error, { store: 'GoalStore' })
    }
  }

  addRetirementGoalAccount = async (account: RetirementGoalAccount) => {
    try {
      const newAccount = await this.api.addRetirementGoalAccount(account)

      runInAction(() => {
        this.retirementGoalAccounts = upsertByGuid(this.retirementGoalAccounts, newAccount)
      })
    } catch (error) {
      console.error('Error occurred while adding retirement goal account:', error)
      this.globalStore.captureException(error, { store: 'GoalStore' })
    }
  }

  deleteGoal = async (guid: string) => {
    try {
      await this.api.removeGoal(guid)

      const index = this.goals.findIndex((g) => g.guid === guid)
      if (index >= 0) {
        this.goals.splice(index, 1)
      }
    } catch (error) {
      console.error('Error occurred while deleting goal:', error)
      this.globalStore.captureException(error, { store: 'GoalStore' })
    }
  }

  deleteRetirementGoal = async (goal: Goal) => {
    try {
      const accounts = this.retirementGoalAccounts.filter(
        (g) => g.retirement_goal_guid === goal.guid,
      )
      for (const account of accounts) {
        await this.deleteRetirementGoalAccount(account.guid)
      }
      await this.api.removeRetirementGoal(goal.guid)

      const index = this.retirementGoals.findIndex((g) => g.guid === goal.guid)
      if (index >= 0) {
        this.retirementGoals.splice(index, 1)
      }
    } catch (error) {
      console.error('Error deleting retirement goal:', error)
      this.globalStore.captureException(error, { store: 'GoalStore' })
    }
  }

  deleteRetirementGoalAccount = async (guid: string) => {
    try {
      await this.api.removeRetirementGoalAccount(guid)

      const index = this.retirementGoalAccounts.findIndex((rga) => rga.guid === guid)
      if (index >= 0) {
        this.retirementGoalAccounts.splice(index, 1)
      }
    } catch (error) {
      console.error('Error deleting retirement goal account:', error)
      this.globalStore.captureException(error, { store: 'GoalStore' })
    }
  }

  reorderGoals = async (goals: Goal[]) => {
    try {
      const reordered = (await this.api.reorderGoals(goals)) as Goal[]

      runInAction(() => {
        for (const goal of reordered) {
          const index = this.goals.findIndex((g) => g.guid === goal.guid)
          if (index >= 0) {
            this.goals[index].position = goal.position
          }
        }
      })
    } catch (error) {
      console.error('Error reordering goals:', error)
      this.globalStore.captureException(error, { store: 'GoalStore' })
    }
  }

  setAlert = (alert: string) => (this.alert = alert)

  setNewRetirementGoalAccounts = (accounts: RetirementGoalAccount[]) => {
    this.newRetirementGoalAccounts = accounts
  }

  setSelectedGoal = (goal: Goal | undefined) => (this.selectedGoal = goal)

  updateGoal = async (goal: Goal) => {
    try {
      const updatedGoal = await this.api.updateGoal(goal)

      runInAction(() => {
        const index = this.goals.findIndex((g) => g.guid === goal.guid)
        if (index >= 0) {
          this.goals[index] = updatedGoal
        }
        if (this.selectedGoal?.guid === goal.guid) {
          this.setSelectedGoal(updatedGoal)
        }
      })
    } catch (error) {
      console.error('Error occurred while updating goal:', error)
      this.globalStore.captureException(error, { store: 'GoalStore' })
    }
  }

  updateRetirementGoal = async (goal: Goal) => {
    try {
      const updatedRetirementGoal = await this.api.updateRetirementGoal({
        ...goal,
        target_amount: goal.amount,
      })

      runInAction(() => {
        const index = this.retirementGoals.findIndex((g) => g.guid === goal.guid)
        if (index >= 0) {
          this.retirementGoals[index] = updatedRetirementGoal
        }

        if (this.selectedGoal?.guid === goal.guid) {
          this.setSelectedGoal(this.allGoals.find((g) => g.guid === goal.guid))
        }
      })
    } catch (error) {
      console.error('Error occurred while updating retirement goal:', error)
      this.globalStore.captureException(error, { store: 'GoalStore' })
    }
  }

  updateMonthlyCashFlowProfile = async (profile: MonthlyCashFlowProfile) => {
    try {
      const updatedProfile = await this.api.updateMonthlyCashFlowProfile(profile)
      this.setMonthlyCashFlowProfile(updatedProfile)
    } catch (error) {
      console.error('Error occurred while updating monthly cash flow profile:', error)
      this.globalStore.captureException(error, { store: 'GoalStore' })
    }
  }
}
